import React, { useState } from 'react';
import { MediaLibraryItem } from '../../../api/media';
import { constructMediaUrl } from '../../../api/helpers';
import { VideoCapabilities } from '../../../api/videoCapabilities';
import MediaLibraryPickerModal from '../../modals/MediaLibraryPickerModal';
import {
  REFERENCE_INPUT_KINDS,
  ReferenceEntry,
  ReferenceInputKind,
  ReferenceInputState,
  entryFromLibraryItem,
  referenceCapOf,
} from './referenceInputs';

interface ReferenceInputsEditorProps {
  state: ReferenceInputState;
  onChange: (next: ReferenceInputState) => void;
  /** The model's parsed video capabilities — the per-kind caps decide which
   *  rows exist at all. A kind the model does not take is never offered. */
  caps: VideoCapabilities;
  /**
   * True when this model declares frame images and reference images mutually
   * exclusive. Only renders the explanation: the HOST owns both states and is
   * the only thing that can clear the other one.
   */
  exclusiveWithFrames?: boolean;
}

const microButton =
  'font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500 hover:text-white transition-colors disabled:text-zinc-700 disabled:hover:text-zinc-700';

const KIND_LABEL: Record<ReferenceInputKind, string> = {
  images: 'Reference images',
  audios: 'Reference audio',
  videos: 'Reference video',
};

const KIND_FILTER: Record<ReferenceInputKind, 'image' | 'audio' | 'video'> = {
  images: 'image',
  audios: 'audio',
  videos: 'video',
};

/** The short line under an entry: the file name, or the media id it names. */
const entryCaption = (entry: ReferenceEntry): string => {
  const tail = (entry.url || entry.value).split('/').pop() || entry.value;
  return tail.length > 18 ? `${tail.slice(0, 17)}…` : tail;
};

interface EntryCardProps {
  kind: ReferenceInputKind;
  entry: ReferenceEntry;
  index: number;
  onRemove: () => void;
}

const EntryCard: React.FC<EntryCardProps> = ({ kind, entry, index, onRemove }) => (
  <div className="w-[4.5rem]" data-testid={`reference-entry-${kind}-${index}`}>
    <div className="aspect-square w-full border border-white/10 bg-black/40 overflow-hidden">
      {kind === 'images' && entry.url ? (
        <img
          src={constructMediaUrl(entry.url)}
          alt={`Reference ${index + 1}`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center px-1 text-center font-mono text-[9px] uppercase tracking-[0.1em] text-zinc-600">
          {kind === 'images' ? 'id only' : kind === 'audios' ? 'audio' : 'video'}
        </div>
      )}
    </div>
    <p
      className="mt-1 truncate font-mono text-[9px] tracking-[0.05em] text-zinc-500"
      title={entry.value}
    >
      {entryCaption(entry)}
    </p>
    <button
      type="button"
      className={microButton}
      onClick={onRemove}
      data-testid={`reference-remove-${kind}-${index}`}
    >
      Remove
    </button>
  </div>
);

/**
 * The reference INPUTS a video generation carries — images, audio, video —
 * shown beside the anchor-frame slots rather than instead of them.
 *
 * Progressive disclosure is the whole rule here: a kind the model does not
 * declare has no row, and a model that declares none of them renders nothing
 * at all. Every count comes from the registry (`caps`), so a cap of 3 offers a
 * fourth "Add" to nobody — the provider's own refusal is not a UI affordance.
 */
const ReferenceInputsEditor: React.FC<ReferenceInputsEditorProps> = ({
  state,
  onChange,
  caps,
  exclusiveWithFrames = false,
}) => {
  const [pickerKind, setPickerKind] = useState<ReferenceInputKind | null>(null);
  const offered = REFERENCE_INPUT_KINDS.filter((kind) => referenceCapOf(caps, kind) > 0);

  if (offered.length === 0) return null;

  const setKind = (kind: ReferenceInputKind, entries: ReferenceEntry[]) =>
    onChange({ ...state, [kind]: entries });

  const addEntry = (kind: ReferenceInputKind, selected: MediaLibraryItem) => {
    const entry = entryFromLibraryItem(selected);
    setPickerKind(null);
    if (!entry) return;
    const current = state[kind];
    // The same subject twice is never what the operator meant, and the
    // provider bills for it either way.
    if (current.some((existing) => existing.value === entry.value)) return;
    if (current.length >= referenceCapOf(caps, kind)) return;
    setKind(kind, [...current, entry]);
  };

  return (
    <div className="space-y-2" data-testid="reference-inputs-editor">
      {offered.map((kind) => {
        const entries = state[kind];
        const cap = referenceCapOf(caps, kind);
        return (
          <div key={kind} className="space-y-1.5">
            <p className="flex items-baseline gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              {KIND_LABEL[kind]}
              <span className="text-zinc-600" data-testid={`reference-count-${kind}`}>
                {entries.length}/{cap}
              </span>
            </p>
            <div className="flex flex-wrap items-start gap-2">
              {entries.map((entry, index) => (
                <EntryCard
                  key={`${entry.value}-${index}`}
                  kind={kind}
                  entry={entry}
                  index={index}
                  onRemove={() =>
                    setKind(
                      kind,
                      entries.filter((_, position) => position !== index)
                    )
                  }
                />
              ))}
              {entries.length < cap && (
                <button
                  type="button"
                  className="w-[4.5rem] aspect-square border border-dashed border-white/15 hover:border-white/40 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-500 hover:text-white transition-colors"
                  onClick={() => setPickerKind(kind)}
                  data-testid={`reference-add-${kind}`}
                >
                  + add
                </button>
              )}
            </div>
          </div>
        );
      })}

      {exclusiveWithFrames && (
        <p
          className="font-mono text-[10px] leading-relaxed text-amber-200/70"
          data-testid="reference-frames-exclusive"
        >
          This model takes either opening/closing frames or references, never both — adding one
          clears the other.
        </p>
      )}

      {pickerKind && (
        <MediaLibraryPickerModal
          isOpen={!!pickerKind}
          onClose={() => setPickerKind(null)}
          filterType={KIND_FILTER[pickerKind]}
          onSelect={(selected: MediaLibraryItem) => addEntry(pickerKind, selected)}
        />
      )}
    </div>
  );
};

export default ReferenceInputsEditor;
